import React, { useEffect, useState } from "react";
import axios from "../api/baseurl";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Pagination } from "react-bootstrap";
import AddInvoicesByLab from "./addInvoicesByLab";
import AddInvoiceTransaction from "./invoiceTransactionPopup";
import { format, parse } from "date-fns";
import InvoiceTransactionsHistory from "./invoiceTransactionsHistory";
import { FiInfo } from "react-icons/fi";
import "./hm-theme.css";

function LabOutgoingInvoices({ labcode }) {
  const [invoices, setInvoices] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loader, setLoader] = useState(true);
  const [invoiceNumberFilter, setInvoiceNumberFilter] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedLab, setSelectedLab] = useState(null);
  const [isTransactionPopupOpen, setIsTransactionPopupOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [isInvoiceTransactionModalOpen, setIsInvoiceTransactionModalOpen] = useState(false);

  const navigate = useNavigate();
  const userRole = JSON.parse(localStorage.getItem("data"))?.role;
  const token = JSON.parse(localStorage.getItem("data"))?.token;

  useEffect(() => {
    if (invoiceNumberFilter || selectedDate || statusFilter !== "all") {
      setCurrentPage(0);
    }
  }, [invoiceNumberFilter, selectedDate, statusFilter]);

  useEffect(() => {
    if (invoiceNumberFilter || selectedDate || statusFilter !== "all") {
      searchData(currentPage);
    } else {
      getData(currentPage);
    }
  }, [invoiceNumberFilter, selectedDate, statusFilter, currentPage]);

  const searchData = async (page = 0) => {
    setLoader(true);
    try {
      const searchUrl = `/api/invoiceSearch/searchLabOrCompanyInvoices?page=${page}`;
      const requestData = {
        invoiceNumber: invoiceNumberFilter || null,
        date: selectedDate || null,
        labId: selectedLab || null,
        status: statusFilter === "all" ? null : statusFilter,
        createdBy: "Lab",
        invoiceType: "outgoing",
      };
      const response = await axios.post(searchUrl, requestData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvoices(response.data.content || []);
      setCurrentPage(response.data.number || 0);
      setTotalPages(response.data.totalPages || 0);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      handleError(error);
    }
  };

  const getData = async (pageNumber = 0) => {
    setLoader(true);
    try {
      const labRes = await axios.get(`/api/labdetails/labcode=${labcode}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedLab(labRes.data?.id);
      const trans = await axios.get(`/api/invoices/getLabOutgoingInvoices/labId=${labRes.data?.id}`, {
        params: { page: pageNumber },
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvoices(trans.data.content || []);
      setCurrentPage(trans.data.number || 0);
      setTotalPages(trans.data.totalPages || 0);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      handleError(error);
    }
  };

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  function handleError(error) {
    if (error.response) {
      if (error.response.status === 401) {
        navigate(`/signin`);
        toast.error("Your session has expired. Please login.");
      } else if (error.response.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.response.data?.code) {
        toast.error(error.response.data.code);
      } else {
        toast.error("An error occurred.");
      }
    } else {
      toast.error(error.message || "An error occurred.");
    }
  }

  const handleCreateInvoice = () => setIsModalOpen(true);
  const handleCloseModal = () => { setIsModalOpen(false); getData(); };

  const handleSearchInvoiceNumber = (e) => setInvoiceNumberFilter(e.target.value.toLowerCase());
  const handleDateChange = (date) => setSelectedDate(date ? format(date, "MM/yyyy") : null);
  const handleStatusFilterChange = (e) => setStatusFilter(e.target.value);

  const handleViewPdf = async (id) => {
    await axios({
      url: `/api/invoices/download/id=${id}`,
      method: "GET",
      responseType: "blob",
      headers: { Authorization: `Bearer ${token}` },
    }).then((response) => {
      const contentType = response.headers["content-type"];
      let extension = contentType.includes("application/pdf") ? "pdf"
        : contentType.includes("image/jpeg") ? "jpg"
        : contentType.includes("image/png") ? "png" : null;
      if (!extension) return;
      const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoice.${extension}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    });
  };

  const handleOpenTransactionHistory = (invoiceId) => {
    setSelectedInvoiceId(invoiceId);
    setIsInvoiceTransactionModalOpen(true);
  };

  const handleCloseInvoiceTransactionModal = () => {
    setIsInvoiceTransactionModalOpen(false);
    setSelectedInvoiceId(null);
    getData(currentPage);
  };

  const handleOpenTransactionPopup = (invoiceId) => {
    setSelectedInvoiceId(invoiceId);
    setIsTransactionPopupOpen(true);
  };

  const handleCloseTransactionModal = () => {
    setIsTransactionPopupOpen(false);
    getData();
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPageNumbersToShow = 5;
    let startPage = Math.max(0, currentPage - 2);
    let endPage = Math.min(totalPages - 1, currentPage + 2);
    if (currentPage <= 2) endPage = Math.min(totalPages - 1, maxPageNumbersToShow - 1);
    if (currentPage >= totalPages - 3) startPage = Math.max(0, totalPages - maxPageNumbersToShow);
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
          {i + 1}
        </Pagination.Item>
      );
    }
    return pageNumbers;
  };

  const getStatusBadge = (status) => {
    if (!status) return <span className="hm-badge hm-badge-warning">—</span>;
    if (status === "Paid") return <span className="hm-badge hm-badge-success">{status}</span>;
    if (status === "Partial Payment") return <span className="hm-badge hm-badge-warning">{status}</span>;
    return <span className="hm-badge hm-badge-primary">{status}</span>;
  };

  return (
    <div>
      {loader && <div className="loading"></div>}

      <div className="hm-card mt-3">
        <div className="hm-card-header">
          <h4 className="hm-card-title">
            <i className="material-icons">send</i>
            Lab Outgoing Invoices
          </h4>
          {(userRole === "ROLE_LAB" || userRole === "ROLE_LAB_CORPORATE") && (
            <button className="btn btn-hm-primary btn-sm" onClick={handleCreateInvoice}>
              <i className="material-icons" style={{ fontSize: "1rem" }}>add</i> Add Invoice
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="hm-card-body border-bottom bg-light bg-opacity-50 p-3">
          <div className="row g-3 align-items-end">
            <div className="col-md-4">
              <label htmlFor="outgoingInvoiceNumberFilter" className="form-label small fw-semibold mb-1">Invoice Number</label>
              <input
                type="search"
                className="form-control"
                id="outgoingInvoiceNumberFilter"
                placeholder="Search by Invoice Number"
                value={invoiceNumberFilter || ""}
                onChange={handleSearchInvoiceNumber}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label small fw-semibold mb-1">Month & Year</label>
              <DatePicker
                selected={selectedDate ? parse(selectedDate, "MM/yyyy", new Date()) : null}
                onChange={handleDateChange}
                showMonthYearPicker
                dateFormat="MM/yyyy"
                className="form-control"
                placeholderText="Select Month and Year"
              />
            </div>
            <div className="col-md-4">
              <label htmlFor="outgoingStatusFilter" className="form-label small fw-semibold mb-1">Status</label>
              <select id="outgoingStatusFilter" className="form-select" value={statusFilter} onChange={handleStatusFilterChange}>
                <option value="all">ALL STATUS</option>
                <option value="Invoice Generated">INVOICE GENERATED</option>
                <option value="Partial Payment">PARTIAL PAYMENT</option>
                <option value="Paid">PAID</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="hm-card-body p-0">
          <div style={{ overflowX: "auto" }}>
            <table className="hm-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Period</th>
                  <th>Invoice No.</th>
                  <th>Gross Amount</th>
                  <th>Tax</th>
                  <th>Net Pay</th>
                  <th>File</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 ? (
                  <tr>
                    <td colSpan="8">
                      <div className="hm-empty-state">
                        <i className="material-icons">send</i>
                        <p>{loader ? "Loading invoices..." : "No Invoices Found"}</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  invoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td className="small text-muted">{invoice.date}</td>
                      <td className="small">{invoice.startdate} — {invoice.enddate}</td>
                      <td className="fw-semibold">{invoice.invoicenumber}</td>
                      <td>₹{invoice.grossamount}</td>
                      <td>{invoice.tax}</td>
                      <td className="fw-semibold">₹{invoice.netpay}</td>
                      <td>
                        {invoice.invoiceFileUploaded === true ? (
                          <button className="btn btn-outline-primary btn-sm rounded-pill px-3" onClick={() => handleViewPdf(invoice.id)}>
                            <i className="material-icons align-middle" style={{ fontSize: "0.9rem" }}>visibility</i> View
                          </button>
                        ) : (
                          <span className="text-muted small">No File</span>
                        )}
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          {getStatusBadge(invoice.invoiceStatus?.status)}
                          {invoice.invoiceStatus?.status === "Paid" && (
                            <FiInfo
                              onClick={() => handleOpenTransactionHistory(invoice.id)}
                              className="text-primary"
                              style={{ cursor: "pointer" }}
                              title="View Transaction History"
                            />
                          )}
                        </div>
                        {invoice.invoiceStatus?.status === "Partial Payment" && (
                          <div className="mt-1">
                            <div className="small text-success fw-semibold">Paid: ₹{invoice.paidAmount}</div>
                            <div className="small text-danger fw-semibold">Pending: ₹{invoice.pendingAmount}</div>
                          </div>
                        )}
                        {invoice.invoiceStatus?.status !== "Paid" && (
                          <div className="mt-2">
                            <button
                              className="btn btn-hm-primary btn-sm rounded-pill"
                              onClick={() => handleOpenTransactionPopup(invoice.id)}
                            >
                              <i className="material-icons align-middle" style={{ fontSize: "0.9rem" }}>payments</i> Payment Received
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="d-flex justify-content-center py-3">
              <Pagination className="hm-pagination">
                <Pagination.First onClick={() => handlePageChange(0)} disabled={currentPage === 0} />
                <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 0} />
                {renderPageNumbers()}
                <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages - 1} />
                <Pagination.Last onClick={() => handlePageChange(totalPages - 1)} disabled={currentPage >= totalPages - 1} />
              </Pagination>
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <AddInvoicesByLab isOpen={isModalOpen} onRequestClose={handleCloseModal} labId={selectedLab} />
      )}
      {isTransactionPopupOpen && (
        <AddInvoiceTransaction isOpen={isTransactionPopupOpen} onRequestClose={handleCloseTransactionModal} invoiceid={selectedInvoiceId} />
      )}
      {isInvoiceTransactionModalOpen && (
        <InvoiceTransactionsHistory isOpen={isInvoiceTransactionModalOpen} onRequestClose={handleCloseInvoiceTransactionModal} invoiceId={selectedInvoiceId} />
      )}
    </div>
  );
}

export default LabOutgoingInvoices;
